import React from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import styled from "@emotion/styled";
import { Header } from "components/DisplayBox";
import { H3 } from "components/Text";
import { Tag } from "components/Tags";
import { Label, Select, SIZE } from "components/Form";
import Icon, { ICON_SIZE, ICON_TYPE } from "components/Icon";
import { TextLoader } from "components/Loader";
import {
  getStateText,
  isActiveTermSheet,
  isDraftTermSheet,
  TERM_SHEET_PRICING_TYPES,
  TERM_SHEET_STATUSES,
  TERMSHEET_STATUS_COLORS,
} from "constants/index";
import { formatDate } from "utils";
import { colors, margins } from "style";
import { useTermSheetContext } from "hooks";
import { LegacyTermSheetAdvancedOptions } from "components/TermSheets";

/**
 * LegacyTermSheetTableHeader
 * @param {Boolean}     loading - External loading state
 * @param {Boolean}     isExpanded - Local UI state for expansion
 * @param {Function}    onToggle - Handler for toggling expansion
 */
const LegacyTermSheetTableHeader = ({ loading, isExpanded, onToggle }) => {
  if (loading) return <Loader isExpanded={isExpanded} />;

  const {
    termSheet,
    hasAnyChanges,
    handleSave,
    saving,
    billingAccounts,
    selectedBillingAccount,
    handleBillingAccountChange,
    updatingBillingAccount,
  } = useTermSheetContext();

  return (
    <StyledHeader isExpanded={isExpanded}>
      <HeaderSection>
        <HeaderContent>
          <ToggleButton onClick={onToggle} type="button" aria-label="Toggle term sheet">
            <Icon
              type={isExpanded ? ICON_TYPE.chevronDown : ICON_TYPE.chevronRight}
              size={ICON_SIZE.normal}
              color={colors.purpleRainBase}
            />
          </ToggleButton>
          <H3>{termSheet?.name}</H3>
        </HeaderContent>
        <Tag
          color={TERMSHEET_STATUS_COLORS[TERM_SHEET_STATUSES[termSheet?.state]]?.background}
          background={TERMSHEET_STATUS_COLORS[TERM_SHEET_STATUSES[termSheet?.state]]?.color}
        >
          <FormattedMessage
            id="Squads.Squad.TermSheet.Table.Header.Tag"
            values={{
              type: TERM_SHEET_PRICING_TYPES[termSheet?.type],
              state: getStateText(termSheet),
            }}
          />
        </Tag>
        {isDraftTermSheet(termSheet) && billingAccounts.length > 0 && (
          <BillingAccountContainer>
            <StyledLabel>
              <FormattedMessage id="TermSheets.Legacy.BillingAccount" defaultMessage="Billing Account" />
            </StyledLabel>
            <StyledSelect
              name="billingAccount"
              size={SIZE.small}
              value={selectedBillingAccount}
              onChange={(_, accountId) => handleBillingAccountChange(accountId)}
              options={billingAccounts?.map(({ id, name, currencyCode }) => ({
                id,
                label: `${name} - (${currencyCode})`,
              }))}
              valueKey="id"
              labelKey="label"
              placeholder="Select billing account..."
              disabled={updatingBillingAccount}
              loading={updatingBillingAccount}
            />
          </BillingAccountContainer>
        )}
        {isActiveTermSheet(termSheet) && (
          <FormattedMessage
            id="Squads.Squad.TermSheet.Table.Header.Date"
            values={{ date: formatDate(termSheet?.createdAt) }}
          />
        )}
      </HeaderSection>
      <HeaderSection>
        {(isDraftTermSheet(termSheet) || isActiveTermSheet(termSheet)) && hasAnyChanges && (
          <SaveButton onClick={handleSave} disabled={saving} type="button">
            <Icon type={ICON_TYPE.checkmark} color={colors} size={ICON_SIZE.xlarge} />
          </SaveButton>
        )}
        <LegacyTermSheetAdvancedOptions termSheet={termSheet} />
      </HeaderSection>
    </StyledHeader>
  );
};

/**
 * Loader component for the header
 */
const Loader = ({ isExpanded }) => (
  <StyledHeader isExpanded={isExpanded}>
    <HeaderSection>
      <HeaderContent>
        <ToggleButton type="button" aria-label="Loading" disabled>
          <Icon type={ICON_TYPE.chevronDown} size={ICON_SIZE.normal} color={colors.grayAnatomyLight3} />
        </ToggleButton>
        <TextLoader width="20rem" />
      </HeaderContent>
      <TextLoader width="8rem" />
    </HeaderSection>
    <HeaderSection>
      <TextLoader width="3rem" />
    </HeaderSection>
  </StyledHeader>
);

Loader.propTypes = {
  isExpanded: PropTypes.bool,
};

const StyledHeader = styled(Header)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${margins.normal};

  ${({ isExpanded }) =>
    !isExpanded &&
    `
    padding-bottom: 0;
    margin-bottom: 0;
    border-bottom: none;
  `}
`;

const HeaderSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${margins.xxsmall};
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ToggleButton = styled.button`
  background: none;
  border: none;
  padding: 0.25rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.7;
  }
`;

const BillingAccountContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${margins.xxsmall};
`;

const StyledLabel = styled(Label)`
  margin-bottom: 0;
  white-space: nowrap;
`;

const StyledSelect = styled(Select)`
  min-width: 250px;
`;

const SaveButton = styled.button`
  display: flex;
  align-items: center;
  background: ${colors.successBase};
  border: 1px solid ${colors.successDark};
  border-radius: 4px;
  color: ${colors.white};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  background: ${colors.green2};
  border-radius: 50%;
  padding: 2px;

  &:hover:not(:disabled) {
    background: ${colors.green};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

LegacyTermSheetTableHeader.propTypes = {
  loading: PropTypes.bool,
  isExpanded: PropTypes.bool,
  onToggle: PropTypes.func,
};

export default LegacyTermSheetTableHeader;
